/**
 * Convite de usuário de empresa (E9, MT-907). Espelha a `CompanyInvitationCreateSerializer`:
 * o e-mail, o papel na empresa e um nível por módulo fiscal.
 *
 * Nome, CPF e senha não estão aqui — são do convidado, e ele os informa no aceite. Os tetos
 * de papel e de nível são do backend (`COMPANY_ROLE_GRANTS`, `assert_level_within_ceiling`);
 * a tela apenas desabilita o que o concedente não tem.
 */
import { z } from 'zod';

import type { CompanyInvitationCreate } from '~/api/companyUsers';
import {
  ACCESS_LEVEL_ORDER,
  COMPANY_ROLE,
  COMPANY_ROLES,
  FISCAL_MODULES,
  type FiscalModuleKey,
} from '~/session/access';

const levelSchema = z.enum(ACCESS_LEVEL_ORDER);

export const companyInvitationSchema = z.object({
  email: z.string().trim().min(1, 'Informe o e-mail de quem você convida').email('E-mail inválido'),
  role: z.enum(COMPANY_ROLES),
  access: z.object({
    nfse: levelSchema,
    nfe: levelSchema,
    nfce: levelSchema,
    documents: levelSchema,
  }),
});

export type CompanyInvitationForm = z.infer<typeof companyInvitationSchema>;

export const COMPANY_INVITATION_FIELDS = ['email', 'role'] as const;

export type CompanyInvitationField = (typeof COMPANY_INVITATION_FIELDS)[number];
export type CompanyInvitationFieldErrors = Partial<Record<CompanyInvitationField, string>>;

/** Ninguém nasce com acesso: quem convida escolhe módulo por módulo. */
export const EMPTY_COMPANY_INVITATION_FORM: CompanyInvitationForm = {
  email: '',
  role: COMPANY_ROLE.member,
  access: { nfse: 'NONE', nfe: 'NONE', nfce: 'NONE', documents: 'NONE' },
};

export interface CheckedCompanyInvitation {
  /** O corpo do `POST`, ou `null` quando algum campo está errado. */
  payload: CompanyInvitationCreate | null;
  errors: CompanyInvitationFieldErrors;
}

function firstMessageByField(
  error: z.ZodError<CompanyInvitationForm>,
): CompanyInvitationFieldErrors {
  const { fieldErrors } = error.flatten();
  const errors: CompanyInvitationFieldErrors = {};
  for (const field of COMPANY_INVITATION_FIELDS) {
    const [message] = fieldErrors[field] ?? [];
    if (message) {
      errors[field] = message;
    }
  }
  return errors;
}

export function checkCompanyInvitation(form: CompanyInvitationForm): CheckedCompanyInvitation {
  const result = companyInvitationSchema.safeParse(form);
  if (!result.success) {
    return { payload: null, errors: firstMessageByField(result.error) };
  }
  return { payload: result.data, errors: {} };
}

/** A recusa de nível do backend (`access.nfse`) no módulo a que pertence. */
export function accessLevelFieldErrors(
  byField: Record<string, string>,
): Partial<Record<FiscalModuleKey, string>> {
  const errors: Partial<Record<FiscalModuleKey, string>> = {};
  for (const module of FISCAL_MODULES) {
    const message = byField[module];
    if (message) {
      errors[module] = message;
    }
  }
  return errors;
}

/** Os erros por campo do backend, só nos campos que esta tela mostra. */
export function companyInvitationFieldErrors(
  byField: Record<string, string>,
): CompanyInvitationFieldErrors {
  const errors: CompanyInvitationFieldErrors = {};
  for (const field of COMPANY_INVITATION_FIELDS) {
    const message = byField[field];
    if (message) {
      errors[field] = message;
    }
  }
  return errors;
}
